<template>
  <SpinnerOverlay />
  <ConfirmDialog />
  <div>
    <nav v-if="showAdminHeader" class="navbar navbar-expand-lg fixed-top custom-navbar">
      <div class="container-fluid">
        <!-- Left section -->
        <div class="d-flex align-items-center">
          <button v-if="hasRequiredRole(['admin'])" @click="toggleSidebar"
            class="btn btn-sm btn-outline-gold me-2 mt-1" type="button">
            <i class="bi bi-list text-gold fs-3"></i>
          </button>
          <router-link to="/" class="navbar-brand text-decoration-none">
            <div class="logo-container">
              <img src="/favicon.ico" alt="Logo" class="d-inline-block align-text-top logo-img" />
            </div>
          </router-link>
        </div>

        <!-- Center section -->
        <div class="text-center flex-grow-1">
          <h1 class="pageant-title d-flex flex-column flex-md-row align-items-center justify-content-center">
            <span class="title-text">
              {{ currentEventTitle !== 'Show Slider' ? currentEventTitle : 'EXODOUS TABULATION SYSTEM' }}
            </span>
            <span class="bi bi-magic magic-icon d-none"></span>
          </h1>
        </div>

        <!-- Right section -->
        <div class="d-flex align-items-center ms-auto">
          <div class="dropdown">
            <a title="Account" class="user-profile d-flex align-items-center me-2 navbar-brand" href="#" id="userDropdown"
              data-bs-toggle="dropdown" aria-expanded="false">
              <i class="bi bi-person-circle me-1 user-icon"></i>
              <span class="username d-none fw-bold d-lg-block">
                {{ currentUser ? currentUser.unique_name.toUpperCase() : "Guest" }}
              </span>
            </a>
            <ul class="dropdown-menu dropdown-menu-end custom-dropdown" aria-labelledby="userDropdown">
              <li>
                <h3 class="dropdown-header text-center user-display-name">
                  {{ currentUser ? currentUser.winaccountname : "" }}
                </h3>
              </li>
              <li v-if="currentUser && hasRequiredRole(['admin', 'web-scan-live'])">
                <a href="#" @click.prevent="toggleLiveScan('live')" class="dropdown-item">
                  <i class="bi bi-qr-code-scan me-1"></i> Live Scan
                </a>
              </li>
              <li v-if="currentUser && hasRequiredRole(['admin', 'web-scan-print'])">
                <a href="#" @click.prevent="toggleLiveScan('print')" class="dropdown-item">
                  <i class="bi bi-qr-code-scan me-1"></i> ID Print Scan
                </a>
              </li>
              <li>
                <hr class="dropdown-divider" />
              </li>
              <li>
                <a href="#" @click.prevent="logout" class="dropdown-item logout-item">
                  <i class="bi bi-box-arrow-left me-1"></i> Logout
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>

    <div class="content-wrapper" id="content-container">
      <div v-if="showAdminHeader && hasRequiredRole(['admin'])"
        :class="['custom-sidebar', 'border-end', 'sidebar-wrapper', { toggled: isSidebarToggled }]" id="sidebar-wrapper">
        <div class="list-group list-group-flush">
          <router-link to="/" class="list-group-item list-group-item-action sidebar-item mt-3">
            <i class="bi bi-speedometer me-2"></i> Dashboard
          </router-link>
          <div class="accordion" id="shortcutsAccordion">
            <div class="accordion-item sidebar-accordion">
              <button class="accordion-button collapsed sidebar-item text-decoration-none" type="button"
                data-bs-toggle="collapse" data-bs-target="#shortcutsCollapse" aria-expanded="false"
                aria-controls="shortcutsCollapse">
                <i class="bi bi-people me-2"></i> Voters
              </button>
              <div id="shortcutsCollapse" class="accordion-collapse collapse" aria-labelledby="shortcutsCollapse"
                data-bs-parent="#shortcutsAccordion">
                <div class="accordion-body sidebar-accordion-body" v-if="currentUser && hasRequiredRole(['admin', 'voters'])">
                  <router-link to="/voters" class="dropdown-item py-2 sidebar-link">
                    <i class="bi bi-people-fill me-2"></i> Voters List
                  </router-link>
                  <router-link v-if="currentUser && hasRequiredRole(['admin', 'households'])" to="/households"
                    class="dropdown-item py-2 sidebar-link">
                    <i class="bi bi-house-door-fill me-2"></i> Households
                  </router-link>
                  <router-link v-if="currentUser && hasRequiredRole(['admin', 'heatmap'])" to="/heatmap"
                    class="dropdown-item py-2 sidebar-link">
                    <i class="bi bi-geo-alt-fill me-2"></i> Heat Map
                  </router-link>
                  <router-link v-if="currentUser && hasRequiredRole(['admin', 'import'])" to="/imports"
                    class="dropdown-item py-2 sidebar-link">
                    <i class="bi bi-person-fill-down me-2"></i> Imports
                  </router-link>
                </div>
              </div>
            </div>
          </div>
          <div class="accordion" id="settingsAccordion">
            <div class="accordion-item sidebar-accordion">
              <button class="accordion-button collapsed sidebar-item text-decoration-none" type="button"
                data-bs-toggle="collapse" data-bs-target="#settingsCollapse" aria-expanded="false"
                aria-controls="settingsCollapse">
                <i class="bi bi-gear me-2"></i> Settings
              </button>
              <div id="settingsCollapse" class="accordion-collapse collapse" aria-labelledby="settingsCollapse"
                data-bs-parent="#settingsAccordion">
                <div class="accordion-body sidebar-accordion-body">
                  <router-link to="/city-setup" v-if="currentUser && hasRequiredRole(['admin', 'city-setup'])"
                    class="dropdown-item py-2 sidebar-link">
                    <i class="bi bi-buildings-fill me-2"></i> Municipal Settings
                  </router-link>
                  <router-link v-if="currentUser && hasRequiredRole(['admin', 'brgy-setup'])" to="/brgy-setup"
                    class="dropdown-item py-2 sidebar-link">
                    <i class="bi bi-house-door-fill me-2"></i> Barangay Settings
                  </router-link>
                  <router-link v-if="currentUser && hasRequiredRole(['admin', 'heatmap-setup'])" to="/map-setup"
                    class="dropdown-item py-2 sidebar-link">
                    <i class="bi bi-gear-fill me-2"></i> Map Settings
                  </router-link>
                  <router-link v-if="currentUser && hasRequiredRole(['admin', 'judge'])" to="/user-setup"
                    class="dropdown-item py-2 sidebar-link">
                    <i class="bi bi-person-fill-gear me-2"></i> User Settings
                  </router-link>
                </div>
              </div>
            </div>
          </div>
          <router-link v-if="currentUser && hasRequiredRole(['admin', 'judge'])" to="/reports"
            class="list-group-item list-group-item-action sidebar-item mt-3">
            <i class="bi bi-file-earmark-bar-graph me-2"></i> Reports
          </router-link>
        </div>
      </div>

      <main id="main-content" class="main-content p-3">
        <slot/>
      </main>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import confirmationService from "@/services/confirmationService";
import SpinnerOverlay from "./animations/SpinnerOverlay.vue";
import ConfirmDialog from "./animations/ConfirmDialog.vue";
import { getUserRoles, getUserObject } from "@/utils/auth";

const isSidebarToggled = ref(false);
const store = useStore();
const router = useRouter();
const currentUser = computed(() => getUserObject());

const showAdminHeader = ref(store.state.showAdminHeader);
const currentEventTitle = computed(() => store.state.currentEventTitle);
const showLiveScan = ref(false);
const scanMode = ref("live");

const hasRequiredRole = (requiredRoles) => {
  return requiredRoles.some(role => getUserRoles().includes(role));
};

const toggleLiveScan = (mode) => {
  scanMode.value = mode;
  showLiveScan.value = !showLiveScan.value;
};

const toggleSidebar = () => {
  isSidebarToggled.value = !isSidebarToggled.value;
};

const logout = async () => {
  const confirmed = await confirmationService.show(
    "Are you sure you want to logout?"
  );
  if (!confirmed) return;

  await store.dispatch("auth/logout");
  store.dispatch("showAdminHeader", false);
  router.push("/login");
};

watch(
  () => store.state.showAdminHeader,
  (newVal) => {
    showAdminHeader.value = newVal;
  }
);

const updateHeaderVisibility = () => {
  const currentRoute = router.currentRoute.value;
  showAdminHeader.value = currentRoute.meta.showHeader !== false;
};

onMounted(() => {
  updateHeaderVisibility();
});

onBeforeUnmount(() => {

});
</script>

<style scoped>
* {
  font-family: 'Poppins', sans-serif;
}

/* Main Navigation */
.custom-navbar {
  background: linear-gradient(135deg, var(--dark-bg) 0%, #1a1a1a 100%) !important;
  border-bottom: 2px solid var(--border-gold);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  padding: 0.5rem 0;
}

/* Logo */
.logo-container {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  padding: 4px;
  border: 1px solid var(--border-gold);
  box-shadow: 0 0 10px var(--shadow-gold);
}

.logo-img {
  width: 50px;
  height: 50px;
  border-radius: 50%;
}

/* Pageant Title */
.pageant-title {
  margin: 0;
}

.title-text {
  font-weight: 700;
  font-size: 1.5rem;
  background: linear-gradient(135deg, var(--text-gold) 0%, var(--gold-light) 50%, var(--gold-primary) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: #d4af37;
  text-shadow: 0 0 20px rgba(212, 175, 55, 0.5);
  letter-spacing: 1px;
}

.magic-icon {
  color: var(--gold-primary);
  filter: drop-shadow(0 0 5px rgba(212, 175, 55, 0.7));
}

/* Buttons */
.btn-outline-gold {
  color: var(--gold-primary);
  border-color: var(--gold-primary);
  background: transparent;
  transition: all 0.3s ease;
}

.btn-outline-gold:hover {
  background-color: var(--gold-primary);
  color: var(--dark-bg);
  box-shadow: 0 0 10px var(--shadow-gold);
}

/* User Profile */
.user-profile {
  color: #d3b75a;
  transition: all 0.3s ease;
  padding: 5px 10px;
  border-radius: 20px;
  background: rgba(121, 104, 55, 0.1);
}

.user-profile:hover {
  background: rgba(212, 175, 55, 0.1);
  color:#8b6914;
}

.user-icon {
  font-size: 1.8rem;
  color: var(--gold-primary);
}

.username {
  color: var(--text-light);
}

/* Dropdown Menu */
.custom-dropdown {
  background: #423f3f;
  border: 1px solid var(--border-gold);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  overflow: hidden;
}

.custom-dropdown .dropdown-item {
  color: var(--text-light);
  padding: 10px 15px;
  transition: all 0.2s ease;
}

.custom-dropdown .dropdown-item:hover {
  background: rgba(212, 175, 55, 0.15);
  color: var(--gold-primary);
}

.user-display-name {
  color: #afab84;
  font-weight: 600;
  padding: 10px 15px;
  border-bottom: 1px solid var(--border-gold);
}

.logout-item {
  color: #ff6b6b !important;
}

.logout-item:hover {
  color: #ff5252 !important;
  background: rgba(255, 107, 107, 0.1) !important;
}

/* Content Wrapper */
.content-wrapper {
  margin-top: 70px;
  height: calc(100vh - 70px);
  display: flex;
  background: var(--dark-bg);
}

/* Sidebar */
.custom-sidebar {
  width: 250px;
  transition: all 0.3s ease;
  height: calc(100vh - 70px);
  background: var(--darker-bg) !important;
  border-right: 1px solid var(--border-gold);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
}

.sidebar-item {
  background: transparent !important;
  color: var(--text-light) !important;
  border: none !important;
  padding: 12px 15px;
  transition: all 0.2s ease;
}

.sidebar-item:hover, .sidebar-item:focus {
  background: rgba(212, 175, 55, 0.1) !important;
  color: var(--gold-primary) !important;
}

.sidebar-accordion {
  background: transparent !important;
  border: none !important;
}

.sidebar-accordion-body {
  background: rgba(0, 0, 0, 0.2);
  padding: 0;
}

.sidebar-link {
  color: var(--text-light) !important;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
}

.sidebar-link:hover, .sidebar-link.router-link-active {
  background: rgba(212, 175, 55, 0.1) !important;
  color: var(--gold-primary) !important;
  border-left: 3px solid var(--gold-primary);
}

/* Main Content */
.main-content {
  flex-grow: 1;
  background: var(--dark-bg);
  color: var(--text-light);
  overflow-y: auto;
}

/* Sidebar Toggle Animation */
.sidebar-wrapper {
  transition: transform 0.3s ease;
}

.sidebar-wrapper.toggled {
  transform: translateX(-100%);
}

/* Mobile Responsiveness */
@media (max-width: 767px) {
  .custom-sidebar {
    width: 250px;
    position: fixed;
    transform: translateX(-100%);
    z-index: 100;
    height: calc(100vh - 70px);
  }

  .custom-sidebar.toggled {
    transform: translateX(0);
  }

  .title-text {
    font-size: 1.2rem;
  }
  
  .user-icon {
    font-size: 1.5rem;
  }
  
  .logo-img {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 575px) {
  .title-text {
    font-size: 1rem;
  }
  
  .content-wrapper {
    margin-top: 60px;
    height: calc(100vh - 60px);
  }
}
</style>
